import type { MapGeometry, MatchMap } from "../model/Analysis";
import { FIGHT_FADE_S } from "./FIGHT_FADE_S";
import type { DeathMark, MapScene, ScenePlayer, ScenePoint } from "./MapScene";
import { smoothTrack } from "./smoothTrack";
import { UV_TO_WORLD, WORLD_SIZE } from "./WORLD_SIZE";

// Драка — смерти не дальше 20 с друг от друга и ближе 140 единиц сцены. Крестик живёт
// до конца драки и ещё `FIGHT_FADE_S` тает.
const FIGHT_GAP_S = 20;
const FIGHT_NEAR = 140;

const world = (value: number): number => value * UV_TO_WORLD;
const fraction = (value: number): number => value * WORLD_SIZE;

// Ответ API (карта матча и геометрия карты) → сцена в единицах рисования.
export const buildMapScene = (match: MatchMap, geometry: MapGeometry): MapScene => {
  const players: ScenePlayer[] = match.players.map((player) => ({
    slot: player.slot,
    team: player.team,
    heroId: player.hero_id,
    track: smoothTrack(
      player.path.map((point): ScenePoint | null =>
        point === null ? null : [world(point[0]), world(point[1]), point[2]],
      ),
    ),
    deaths: player.deaths,
  }));
  const me = players.find((player) => player.slot === match.player_slot);
  if (me === undefined) {
    throw new Error(`в карте матча нет игрока ${match.player_slot}`);
  }
  const teamOf = new Map(match.players.map((player) => [player.slot, player.team]));
  const myPath = match.players.find((player) => player.slot === match.player_slot)?.path ?? [];

  return {
    durationS: match.duration_s,
    stepS: match.step_s,
    team: match.team,
    me,
    others: players.filter((player) => player !== me),
    heatPath: myPath.map((point) =>
      point === null ? null : ([world(point[0]), world(point[1])] as const),
    ),
    deathMarks: groupFights(
      match.deaths.map((death) => ({
        t: death.at_s,
        x: world(death.u),
        y: world(death.v),
        kind:
          death.victim_slot === match.player_slot
            ? "me"
            : death.killer_slot === match.player_slot
              ? "kill"
              : "other",
        victimTeam: teamOf.get(death.victim_slot) ?? 0,
        until: death.at_s,
      })),
    ),
    camps: match.camps.map((camp) => ({
      x: world(camp.u),
      y: world(camp.v),
      kind: camp.kind,
      status: camp.status,
      iconUrl: geometry.camps.find((known) => known.kind === camp.kind)?.icon_url ?? null,
    })),
    objectives: match.objectives.map((objective) => ({
      x: world(objective.u),
      y: world(objective.v),
      team: objective.team,
      kind: objective.kind,
      destroyedAtS: objective.destroyed_at_s,
    })),
    midBoss: match.mid_boss,
    runes: match.runes.map((rune) => ({
      x: world(rune.u),
      y: world(rune.v),
      from: rune.spawned_at_s,
      to: rune.taken_at_s ?? match.duration_s,
    })),
    ziplines: geometry.ziplines.map((zipline) =>
      zipline.points.map(([u, v]) => [fraction(u), fraction(v)] as const),
    ),
    shops: geometry.shops.map((shop) => [fraction(shop.u), fraction(shop.v)] as const),
    sentries: geometry.base_sentries.map((sentry) => ({
      x: fraction(sentry.u),
      y: fraction(sentry.v),
      team: sentry.team ?? 0,
    })),
    minimapUrl: geometry.minimap_url,
  };
};

// Склеивает смерти в драки (см. константы) и проставляет, до какого момента виден крестик.
const groupFights = (marks: DeathMark[]): DeathMark[] => {
  const fights: { end: number; marks: DeathMark[] }[] = [];
  for (const mark of [...marks].sort((a, b) => a.t - b.t)) {
    const fight = fights.find(
      (candidate) =>
        mark.t - candidate.end <= FIGHT_GAP_S &&
        candidate.marks.some(
          (other) => Math.hypot(other.x - mark.x, other.y - mark.y) < FIGHT_NEAR,
        ),
    );
    if (fight === undefined) {
      fights.push({ end: mark.t, marks: [mark] });
    } else {
      fight.marks.push(mark);
      fight.end = mark.t;
    }
  }
  return fights.flatMap((fight) =>
    fight.marks.map((mark) => ({ ...mark, until: fight.end + FIGHT_FADE_S })),
  );
};
